/**
 * "Which PO?" (TASK-0236, INC-8): an invoice whose vendor has an open order, or a PO checked in
 * with no invoice yet, asks whether it belongs to one of them before it makes a new PO.
 *
 * One step, shared by the inbox review, Record invoice and the PO page's Receive tab on a draft.
 * The likely PO is preselected (the server's `preselectPoId`), never chosen silently: the operator
 * either records the invoice on the chosen PO, or says it is a different delivery.
 */
import { ClipboardList } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Callout } from '@/ui/Callout';
import { candidateLabel } from './api';
import type { OpenOrderChoice, OrderCandidateOut } from './types';

export type WhichOrderAnswer = { kind: 'order'; poId: number } | { kind: 'different' };

export function WhichOrderStep({
  vendorName,
  candidates,
  selectedPoId,
  onSelect,
  differentChosen = false,
  orderActionLabel = (poId) => `Record on PO #${poId}`,
  onUseOrder,
  onDifferent,
  busy = false,
  disabledReason = null,
  quantities = true,
  className,
}: {
  vendorName: string | null;
  candidates: (OpenOrderChoice | OrderCandidateOut)[];
  /** The ticked candidate (the server's preselect to start with), or null. */
  selectedPoId: number | null;
  onSelect: (poId: number) => void;
  /** True once "A different delivery" was chosen (the button shows pressed). */
  differentChosen?: boolean;
  orderActionLabel?: (poId: number) => string;
  onUseOrder: (poId: number) => void;
  onDifferent: () => void;
  busy?: boolean;
  /** Why "Record on PO" cannot be used right now (for example "Re-match to World Cheese first"). */
  disabledReason?: string | null;
  /** False when the invoice's lines were typed (the score then has no quantities). */
  quantities?: boolean;
  className?: string;
}) {
  if (candidates.length === 0) return null;
  const vendor = vendorName?.trim() || 'this vendor';
  return (
    <Callout
      tone="info"
      icon={<ClipboardList />}
      title={`Is this the invoice for one of ${vendor}’s orders?`}
      className={className}
    >
      <div className="space-y-2">
        <div role="radiogroup" aria-label="Which PO" className="space-y-1">
          {candidates.map((candidate) => {
            const checked = selectedPoId === candidate.poId;
            return (
              <button
                key={candidate.poId}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() => onSelect(candidate.poId)}
                className={cn(
                  'flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left text-xs transition-colors',
                  checked
                    ? 'border-signal bg-signal-muted text-ink'
                    : 'border-line bg-surface-sunken text-ink hover:border-ink-faint'
                )}
              >
                <span
                  className={cn(
                    'flex size-3 shrink-0 items-center justify-center rounded-full border',
                    checked ? 'border-signal' : 'border-line-strong'
                  )}
                >
                  {checked ? <span className="bg-signal size-1.5 rounded-full" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  {candidateLabel(candidate, quantities)}
                </span>
              </button>
            );
          })}
        </div>
        {disabledReason ? <p className="text-warn text-2xs">{disabledReason}</p> : null}
        <div
          role="group"
          aria-label="Is this invoice for that PO"
          className="flex flex-wrap items-center gap-1.5"
        >
          <Button
            size="xs"
            variant="primary"
            disabled={selectedPoId === null || busy || disabledReason !== null}
            loading={busy}
            onClick={() => {
              if (selectedPoId !== null) onUseOrder(selectedPoId);
            }}
          >
            {selectedPoId === null ? 'Pick a PO' : orderActionLabel(selectedPoId)}
          </Button>
          <Button
            size="xs"
            variant={differentChosen ? 'primary' : 'default'}
            aria-pressed={differentChosen}
            disabled={busy}
            onClick={onDifferent}
          >
            A different delivery
          </Button>
        </div>
        {differentChosen ? (
          <p className="text-2xs text-ink-faint">
            Recorded as a separate delivery from{' '}
            {candidates.map((c) => `PO #${c.poId}`).join(', ')}.
          </p>
        ) : null}
      </div>
    </Callout>
  );
}
